// El registro de terreno de un informe sobre TanStack Query.
//
// UNA SOLA CONSULTA alimenta el contador de la barra y la pestaña: comparten la
// misma key, así que abrir la pestaña no pide nada que la barra no tenga ya.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useResaltadoDeSincronizaciones } from "@/hooks/useResaltadoDeSincronizaciones";
import {
  fetchRegistroDeTerreno,
  marcarSincronizacionRevisada,
  revisarNotasGenerales,
  type NotasGeneralesDeTerreno,
  type RegistroDeTerrenoDelInforme,
} from "@/lib/terreno";

/** Anidada bajo `["informes"]` para que invalidar los informes la alcance. */
export const registroDeTerrenoKey = (informeId: number) =>
  ["informes", "terreno", informeId] as const;

/**
 * Lo que el encargado sincronizó desde la App de terreno.
 *
 * `null` apaga la consulta: es como la pantalla dice "este informe todavía no
 * admite el registro" (antes de la aceptación el API responde 422 y no tiene
 * sentido preguntarlo).
 */
export function useRegistroDeTerreno(informeId: number | null) {
  return useQuery({
    queryKey: registroDeTerrenoKey(informeId ?? 0),
    queryFn: () => fetchRegistroDeTerreno(informeId as number),
    enabled: informeId !== null,
  });
}

/**
 * "Marcar como revisado": guarda hasta qué sincronización vio ESTE usuario.
 *
 * La respuesta es la vista fresca, y reemplaza el caché de la MISMA key: así la
 * pestaña, el contador de la barra y cada panel de terreno de las demás pestañas
 * dejan de decir "nueva" a la vez, sin una segunda petición.
 */
export function useMarcarRevisado(informeId: number) {
  const queryClient = useQueryClient();
  const resaltado = useResaltadoDeSincronizaciones();

  return useMutation({
    mutationFn: (sincronizacionId: string) =>
      marcarSincronizacionRevisada(informeId, sincronizacionId),
    onSuccess: (vista) => {
      queryClient.setQueryData(registroDeTerrenoKey(informeId), vista);
      // Lo revisado ya no se resalta: la elección vuelve a «ninguna».
      resaltado.reiniciar();
    },
  });
}

/**
 * «Revisadas» de las notas generales, OPTIMISTA: el chip se mueve en el acto y
 * vuelve atrás si el servidor falla. Escribe en la MISMA caché que lee la barra,
 * la pestaña y el cajón, así que los tres cambian a la vez.
 */
export function useRevisarNotasGenerales(informeId: number) {
  const queryClient = useQueryClient();
  const key = registroDeTerrenoKey(informeId);

  const escribir = (cambio: (n: NotasGeneralesDeTerreno) => NotasGeneralesDeTerreno) =>
    queryClient.setQueryData<RegistroDeTerrenoDelInforme>(key, (registro) =>
      registro ? { ...registro, notas_generales: cambio(registro.notas_generales) } : registro,
    );

  return useMutation({
    mutationFn: (revisadas: boolean) => revisarNotasGenerales(informeId, revisadas),
    onMutate: async (revisadas) => {
      await queryClient.cancelQueries({ queryKey: key });
      const anterior = queryClient.getQueryData<RegistroDeTerrenoDelInforme>(key);
      escribir((n) => ({ ...n, revisadas }));

      return { anterior };
    },
    onError: (_error, _revisadas, contexto) => {
      if (contexto?.anterior) queryClient.setQueryData(key, contexto.anterior);
    },
    onSuccess: (bloque) => escribir(() => bloque),
  });
}
